﻿import { Dispatch } from 'redux';
import { SettingsState } from '../../model/admin/settingsState';
import { DailyMetricCardVisibility } from '../../model/config/DailyMetricCardVisibility';
import { DailyParameterCardVisibility } from '../../model/config/DailyParameterCardVisibility';
import { OtherCardVisibility } from '../../model/config/OtherCardVisibility';
import { settingsService } from '../../services/settingsService';
import { ThunkAppDispatch } from '../configureStore';
import {
  FETCH_SETTINGS,
  FETCH_SETTINGS_FAILURE,
  FETCH_SETTINGS_SUCCESS,
  SAVE_USER_SETTING,
  SAVE_USER_SETTING_FAILURE,
  SAVE_USER_SETTING_SUCCESS,
  UPDATE_USER_SETTING,
} from '../constants';
import { SettingsActionTypes } from '../types/settingsActionTypes';

export function fetchSettings(): SettingsActionTypes {
  return {
    type: FETCH_SETTINGS,
  };
}

export function fetchSettingsSuccess(data: any) {
  return {
    type: FETCH_SETTINGS_SUCCESS,
    payload: data,
  };
}

export function fetchSettingsError(error: string): SettingsActionTypes {
  return {
    type: FETCH_SETTINGS_FAILURE,
    payload: error,
  };
}

interface Setting {
  id: number;
  name: string;
  value: string;
}

interface Setting {
  id: number;
  name: string;
  value: string;
}

interface Setting {
  id: number;
  name: string;
  value: string;
}

export function getSettings(accountGuid: string) {
  return function action(dispatch: Dispatch<any>) {
    dispatch(fetchSettings());
    return settingsService
      .getEnvironment()
      .then((environmentResponse: Setting[]) => {
        const environmentSettings = environmentResponse || [];
        settingsService.getUser(accountGuid).then((userResponse: Setting[]) => {
          const userSettings = userResponse || [];

          // Filter relevant settings
          const relevantSettings = [
            'DailyMetricsVisibility',
            'DailyParametersVisibility',
            'OtherVisibility',
          ];

          // Create a map of user settings for quick lookup
          const userSettingsMap = new Map(
            userSettings
              .filter((setting: Setting) =>
                relevantSettings.includes(setting.name)
              )
              .map((setting: Setting) => [setting.name, setting])
          );

          // Merge environment settings into user settings
          const mergedSettings = environmentSettings
            .filter((envSetting: Setting) =>
              relevantSettings.includes(envSetting.name)
            )
            .map((envSetting: Setting) => {
              const userSetting = userSettingsMap.get(envSetting.name);
              if (userSetting) {
                const envValue = JSON.parse(envSetting.value);
                const userValue = JSON.parse(userSetting.value);
                // Create a merged value where environment settings override user settings
                // If an environment setting is false, it forces the final value to false
                // If an environment setting is true, the user setting is respected
                const mergedValue = Object.keys(envValue).reduce((result, key) => {
                  // If env is false, always use false regardless of user setting
                  // If env is true, use the user setting (which defaults to false if not set)
                  result[key] = envValue[key] ? (userValue[key] || false) : false;
                  return result;
                }, {} as Record<string, boolean>);
                return {
                  ...envSetting,
                  value: JSON.stringify(mergedValue),
                };
              }
              return envSetting;
            });

          // Add any user settings that are not in the environment settings
          userSettings
            .filter((userSetting: Setting) =>
              relevantSettings.includes(userSetting.name)
            )
            .forEach((userSetting: Setting) => {
              if (
                !mergedSettings.some(
                  (setting) => setting.name === userSetting.name
                )
              ) {
                mergedSettings.push(userSetting);
              }
            });

          dispatch(
            fetchSettingsSuccess({
              environment: environmentSettings,
              user: mergedSettings,
            })
          );
        });
      });
    // error => dispatch(someerror('woody', buzz, error))
  };
}

export const updateUserSetting = (
  category: keyof SettingsState,
  key: string,
  value: boolean
) => {
  return (dispatch: ThunkAppDispatch) => {
    return new Promise<void>((resolve, reject) => {
      try {
        dispatch({
          type: UPDATE_USER_SETTING,
          payload: { category, key, value },
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };
};

export function saveUserSetting(): SettingsActionTypes {
  return {
    type: SAVE_USER_SETTING,
  };
}

export function saveUserSettingsSuccess() {
  return {
    type: SAVE_USER_SETTING_SUCCESS,
  };
}

export function saveUserSettingsError(error: string): SettingsActionTypes {
  return {
    type: SAVE_USER_SETTING_FAILURE,
    payload: error,
  };
}

type VisibilityTypes =
  | DailyMetricCardVisibility
  | DailyParameterCardVisibility
  | OtherCardVisibility;

export function saveUserSettings(
  accountId: string,
  name: keyof SettingsState,
  value: VisibilityTypes
) {
  return function action(dispatch: Dispatch<any>) {
    dispatch(saveUserSetting());
    const stringValue = JSON.stringify(value);
    return settingsService.userSave(accountId, name, stringValue).then(
      (response: any) => dispatch(saveUserSettingsSuccess()),
      (error: string) => dispatch(saveUserSettingsError(error))
    );
  };
}
